<template>
  <div class="reply-item flex overflow-hidden">
    <!-- 左侧头像 -->
    <div class="left-avatar mr-2" style="">
      <UserAvatar
        :image-url="replyItem.UserAvatar"
        :data-user-id="replyItem.UserID"
        class="cursor-pointer"
        size="small"
        @mouseenter.native="onEnter"
        @mouseleave.native="onLeave"
      />
    </div>

    <div class="right-main flex-1 flex flex-col overflow-hidden">
      <!-- 回复顶部信息 -->
      <div
        class="flex justify-between teld-color-gray"
        :class="[$commentOptions.dense || true ? 'mb-1' : 'mb-2']"
      >
        <div class="flex items-center">
          <span class="teld-color-gray font-semibold">
            {{ userNameDisplay }}
          </span>
          <SvgTriangle
            v-if="replyItem.RespondedUserID"
            width="8"
            height="8"
            class="mx-2"
          />
          <span
            v-if="replyItem.RespondedUserID"
            class="teld-color-gray font-semibold"
          >
            {{ replyItem.RespondedUserName }}
          </span>
        </div>
        <!-- <span :title="replyItem.ReplyTime" class="teld-color-gray">{{ replyItem.ReplyTimeRelative }}</span> -->
      </div>

      <!-- 回复内容行 -->
      <div
        class="reply-content"
        :class="[$commentOptions.dense || true ? 'mb-1' : 'mb-2']"
      >
        <div
          ref="replyContentRef"
          v-if="!replyItem.IsDeleted"
          v-html="decodeURIComponent(replyItem.Content)"
          @click="onClickContent"
        ></div>
        <div v-else class="teld-color-gray">该条评论回复已删除</div>
      </div>

      <!-- 附件 -->
      <section
        v-if="!replyItem.IsDeleted && replyItem.Attachments.length"
        class="attachments-section -ml-0.5"
        :class="[$commentOptions.dense ? 'mb-1' : 'mb-2']"
      >
        <div
          v-for="attachment in replyItem.Attachments"
          :key="attachment.ID"
          class="
            attachment-file flex items-center
            hover:text-theme-color cursor-pointer
          "
          :class="[$commentOptions.dense ? '' : 'py-1']"
          @click="handleDownloadFile(attachment)"
        >
          <!-- 文件类型图标 -->
          <FileTypeIcon :file-name="attachment.FileName" class="w-5 h-5 mr-2" />
          <!-- 文件名 -->
          <div
            class="flex-1 truncate overflow-hidden"
            :title="attachment.FileName"
          >
            {{ attachment.FileName }}
          </div>
        </div>
      </section>

      <!-- 回复底部按钮行 -->
      <div class="flex justify-between">
        <!-- 相对时间 -->
        <span :title="replyItem.ReplyTime" class="teld-color-gray">
          {{ replyItem.ReplyTimeRelative }}
        </span>
        <div
          class="reply-operations flex justify-end"
          :class="[disabled ? 'teld-color-lightgray' : 'teld-color-gray']"
        >
          <!-- TODO 各点击按钮是否加 Tab 导航 :tabindex="disabled ? -1 : 0" outline-on-focus -->
          <!-- 点赞 -->
          <div
            class="reply-like flex outline-on-focus rounded-sm leading-5 hover:text-theme-color"
            :class="[disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
            @click="onClickLike"
          >
            <SvgHeartFilled v-show="replyItem.Liked" class="mr-1" />
            <SvgHeart v-show="!replyItem.Liked" class="mr-1" />
            <span class="">{{ replyItem.LikeCount || 0 }}</span>
          </div>
          <!-- 收藏 -->
          <div
            class="reply-star flex ml-2 outline-on-focus rounded-sm leading-5 hover:text-theme-color hidden"
            :class="[disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
            @click="onClickFavorite"
          >
            <SvgStarFilled v-show="replyItem.IsFavorite" class="mr-1" />
            <SvgStar v-show="!replyItem.IsFavorite" class="mr-1" />
            <span class="">
              {{ replyItem.FavoriteCount || 0 }}
            </span>
          </div>
          <!-- 回复 -->
          <div
            class="reply-reply ml-2 outline-on-focus rounded-sm hover:text-theme-color"
            :class="[
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              inputVisible ? 'teld-theme-color' : 'teld-color-gray',
            ]"
            @click="onClickReply"
          >
            {{ inputVisible ? '取消回复' : '回复' }}
          </div>
          <!-- 删除 -->
          <tp-popover v-if="replyItem.CanCancel && !disabled">
            <div class="popover-content p-4" style="min-width:160px">
              <div class="mb-2">确定要删除吗？</div>
              <div class="flex justify-end">
                <span
                  tabindex="0"
                  class="mr-2 cursor-pointer"
                  @click="hideConfirm"
                >
                  取消
                </span>
                <span
                  tabindex="0"
                  class="teld-theme-color cursor-pointer"
                  @click="onClickRevoke"
                >
                  确定
                </span>
              </div>
            </div>

            <div
              slot="reference"
              class="reply-revoke ml-2 outline-on-focus rounded-sm hover:text-theme-color"
              :class="[disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
            >
              删除
            </div>
          </tp-popover>
        </div>
      </div>

      <!-- 评论的回复框 -->
      <draft-editor
        ref="draftEditorRef"
        :visible="inputVisible"
        :placeholder="inputPlaceholder"
        class="w-full transition-all duration-300 overflow-hidden"
        :class="[
          inputVisible ? ($commentOptions.dense ? 'my-1' : 'my-2') : 'h-0',
        ]"
        @comment="onReplyToReply"
      />
    </div>

    <tp-image
      v-if="currentImgSrc"
      ref="imgPreviewRef"
      class="w-0 h-0"
      :src="currentImgSrc"
      :preview-src-list="previewSrcList"
      @load="onImageLoad"
    ></tp-image>
  </div>
</template>

<script>
// Components
import SvgHeart from '../../svg-icons/heart.vue';
import SvgHeartFilled from '../../svg-icons/heart-filled.vue';
import SvgStar from '../../svg-icons/star.vue';
import SvgStarFilled from '../../svg-icons/star-filled.vue';
import SvgTriangle from '../../svg-icons/triangle.vue';
import DraftEditor from '../draft-editor/index.vue';
import UserAvatar from '../../user-avatar.vue';
import FileTypeIcon from '../../file-type-icon/index.vue';

// Utils
import { relativeTime, saveAs, MENTIONED_USER_CLASS_NAME } from '../../../core';
import dayjs from 'dayjs';

export default {
  name: 'ReplyItem',
  inject: [
    'loginUserInfo',
    'pageUrl',
    'showPersonnelInfo',
    'hidePersonnelInfo',
    'notificationLink',
  ],
  props: {
    /**
     * Reply数据条目
     */
    dataItem: {
      type: Object,
      default: () => {},
    },
    isCommentDeleted: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SvgHeart,
    SvgHeartFilled,
    SvgStar,
    SvgStarFilled,
    SvgTriangle,
    DraftEditor,
    // RevokePopover,
    UserAvatar,
    FileTypeIcon,
  },
  data() {
    return {
      // isLoading: true,
      inputVisible: false,
      replyItem: {},
      revokeConfirmVisible: false,
      previewSrcList: [],
      currentImgSrc: '',
    };
  },
  computed: {
    disabled() {
      return this.isCommentDeleted || this.replyItem.IsDeleted;
    },
    showReplyTo() {
      // TODO
      return true;
    },
    isAuthor() {
      const loginUser = this.loginUserInfo();
      return loginUser.userId === this.replyItem.UserID;
    },
    userNameDisplay() {
      return this.isAuthor ? '我' : this.replyItem.UserName;
    },
    inputPlaceholder() {
      return `回复 ${this.userNameDisplay}`;
    },
  },
  created() {
    const { ReplyTime } = this.dataItem;
    const timeRelative = relativeTime(ReplyTime);
    this.replyItem = {
      ...this.dataItem,
      ReplyTime: dayjs(ReplyTime).format('YYYY/MM/DD HH:mm:ss'),
      ReplyTimeRelative: timeRelative || '',
    };
  },
  mounted() {
    this.initImagePreview();
    this.initPersonnelInfo();
  },
  methods: {
    initImagePreview() {
      const elImages = this.$el.querySelectorAll('.reply-content img');
      this.previewSrcList = [].map.call(elImages, elImage => {
        return elImage.src;
      });
    },
    initPersonnelInfo() {
      const replyContentRef = this.$refs.replyContentRef;
      if (!replyContentRef) return;
      const elMentionedUsers = replyContentRef.querySelectorAll(
        `.${MENTIONED_USER_CLASS_NAME}`,
      );
      elMentionedUsers.forEach(el => {
        el.addEventListener('mouseenter', this.onEnter);
        el.addEventListener('mouseleave', this.onLeave);
      });
      console.log(`initPersonnelInfo`, elMentionedUsers);
    },
    onEnter(e) {
      this.showPersonnelInfo({ target: e.target });
    },
    onLeave() {
      this.hidePersonnelInfo();
    },
    async onClickLike() {
      if (this.disabled) return;
      const isLike = (this.replyItem.Liked = !this.replyItem.Liked);
      const diff = this.replyItem.Liked ? 1 : -1;
      this.replyItem.LikeCount += diff;
      let succeeded = false;

      const payload = isLike
        ? { CommentID: this.replyItem.CommentID, TargetID: this.replyItem.ID }
        : { Id: this.replyItem.CommentID, TargetID: this.replyItem.ID };
      const serviceCode = isLike ? 'CMTS-Like' : 'CMTS-CancelLike';
      try {
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl(serviceCode),
          data: isLike
            ? { comment: JSON.stringify(payload) }
            : { input: JSON.stringify(payload) },
        });

        if (data.state !== '1') {
          return;
        }

        succeeded = true;
        // this.$TP.$message.success(isLike ? '已点赞' : '已取消点赞');
      } catch (error) {
        console.log(error);
      } finally {
        // 接口调用失败后，页面状态回滚
        if (!succeeded) {
          this.replyItem.Liked = !isLike;
          this.replyItem.LikeCount -= diff;
        }
      }
    },
    async onClickFavorite() {
      if (this.disabled) return;
      this.replyItem.IsFavorite = !this.replyItem.IsFavorite;
      const diff = this.replyItem.IsFavorite ? 1 : -1;
      this.replyItem.FavoriteCount += diff;
    },
    onClickReply() {
      if (this.disabled) return;
      this.inputVisible = !this.inputVisible;
    },
    /** 使用 tp-popover 时，用于隐藏弹出的气泡 */
    hideConfirm() {
      document.body.click();
    },
    /** TODO 使用自定义的RevokeConfirm组件时用 */
    toggleRevokeConfirm() {
      this.revokeConfirmVisible = !this.revokeConfirmVisible;
    },
    async onClickRevoke() {
      if (this.disabled) return;
      this.hideConfirm();
      this.inputVisible = false;
      this.replyItem.IsDeleted = true;

      try {
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl('CMTS-CancelReply'), //+ `&id=${this.comment.ID}`,
          // method: 'GET',
          data: { id: this.replyItem.ID },
        });

        if (data.state !== '1') {
          this.replyItem.IsDeleted = false;
          this.replyItem.CanCancel = false;
          this.$TP.$message.error(data.errmsg);
          return;
        }
        this.$TP.$message.success('已删除');
        this.$emit('delete', this.replyItem);
      } catch (error) {
        this.replyItem.IsDeleted = false;
        this.replyItem.CanCancel = false;
        this.$TP.$message.error('删除失败');
        console.log(error);
      }
    },
    async onReplyToReply(draftInfo) {
      const loginUser = this.loginUserInfo();
      const { content, fileList, userIdList, onSucceed, onFail } = draftInfo;
      const newCommentReply = {
        // 接口必传字段：
        CommentID: this.replyItem.CommentID,
        Content: content,
        UserAvatar: loginUser.avatar,
        // RespondedUserID: this.replyItem.UserID,
        // RespondedUserName: this.replyItem.UserName,
        TargetID: this.replyItem.ID,
        Attachments: fileList.map(n => n.result),
        MentionedUsers: JSON.stringify(userIdList),
        Url: this.notificationLink(),
        UrlParams: JSON.stringify({
          fpath: encodeURIComponent(this.pageUrl),
          showComment: true,
          commentId: this.replyItem.CommentID,
        }),
        // TODO
        // Attachments: [
        //   {
        //     "CommentID": null,
        //     "AttachmentType": 2,
        //     "AttachmentUrl": "http://resource.teld3.xyz/telddoc/160/ae193012d0ed44bc8f535bb1b997ab5f.docx",
        //     "UploadTime": "2024-02-27T14:56:15.0019732+08:00",
        //     "FileName": "附件上传的方式对比.docx",
        //     "ID": "0934423e-4cd3-4fa7-9861-2e39261075a1",
        //     "Creator": null,
        //     "CreateTime": "0001-01-01T00:00:00",
        //     "LastModifier": null,
        //     "LastModifyTime": "0001-01-01T00:00:00"
        //   },
        //   {
        //     "CommentID": null,
        //     "AttachmentType": 2,
        //     "AttachmentUrl": "http://resource.teld3.xyz/telddoc/160/c589cc00324d4a29963aefcd33424fb5.zip",
        //     "UploadTime": "2024-02-27T14:56:05.4400198+08:00",
        //     "FileName": "empty-新建压缩(zipped)文件夹.zip",
        //     "ID": "c7d0a09a-48bd-4774-b62c-86c451128c1d",
        //     "Creator": null,
        //     "CreateTime": "0001-01-01T00:00:00",
        //     "LastModifier": null,
        //     "LastModifyTime": "0001-01-01T00:00:00"
        //   },
        //   {
        //     "CommentID": null,
        //     "AttachmentType": 0,
        //     "AttachmentUrl": "http://resource.teld3.xyz/teldimage/160/d46fdb8df2b6482982818feddf44f083.png",
        //     "UploadTime": "2024-02-27T14:55:45.6408044+08:00",
        //     "FileName": "F4D2D99C-AB85-4ff7-BDC0-7CB5803C0341.png",
        //     "ID": "434540c6-6a00-4639-ad22-0b8630f5efcc",
        //     "Creator": null,
        //     "CreateTime": "0001-01-01T00:00:00",
        //     "LastModifier": null,
        //     "LastModifyTime": "0001-01-01T00:00:00"
        //   },
        // ],
      };
      // 回复给
      if (loginUser.userId !== this.replyItem.UserID) {
        newCommentReply.RespondedUserID = this.replyItem.UserID;
        newCommentReply.RespondedUserName = this.replyItem.UserName;
      }

      try {
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl('CMTS-SaveReply'),
          data: { reply: JSON.stringify(newCommentReply) },
        });
        console.log('SaveReply', data);
        if (data.state !== '1' || !data.data) {
          onFail();
          return;
        }

        // TODO 接口没有返回新数据对象，暂时用前端数据
        Object.assign(newCommentReply, {
          ID: 'comment-reply-m-' + parseInt(Math.random() * 1000), // TODO UI-mock
          ReplyTime: Date().toLocaleString(), // TODO UI-mock
          UserID: loginUser.userId, // TODO UI-mock
          UserName: loginUser.userName, // TODO UI-mock
          Liked: false, // TODO UI-mock
          LikeCount: 0, // TODO UI-mock
          IsFavorite: false, // TODO UI-mock
          FavoriteCount: 0, // TODO UI-mock
          IsDeleted: false, // TODO UI-mock
          CanCancel: true, // TODO UI-mock
        });

        // 调接口成功后清空输入框内容
        onSucceed();
        this.inputVisible = false;
        // 评论完将新添加评论展示出来
        this.$emit('reply', (data && data.data) || newCommentReply);
      } catch (error) {
        console.log(error);
        onFail();
      }
    },
    onImageLoad(e) {
      e.target.click();
    },
    onClickContent(e) {
      const el = e.target;
      if (el.nodeName === 'IMG' && el.currentSrc) {
        this.currentImgSrc = '';
        this.$nextTick(() => {
          this.currentImgSrc = el.currentSrc;
        });
      }
    },
    handleDownloadFile(attachment) {
      const { AttachmentUrl, FileName } = attachment;
      saveAs({
        url: AttachmentUrl,
        fileName: FileName,
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
